// Legal pages: the Falcon owner's legal details (Console → Legal details), the checks on them, version numbers and the
// four public documents (Terms of service, Privacy policy, Data processing agreement, Sub-processors) written from them.
// Used by the server (publishing a version, the public /legal pages) and the screens (the details form, the preview).
//
// Rules for the wording: plain English; nothing is ever left blank, in brackets or as a placeholder. A missing company
// name reads "the operator of Falcon PLM"; a sentence that needs a missing detail is left out (governing law only when a
// country is given; registration and VAT/tax numbers only when filled in). Nothing is tied to one country.

export type LegalDocId = "terms" | "privacy" | "dpa" | "subprocessors";
export const LEGAL_DOCS: Array<{ id: LegalDocId; title: string; short: string }> = [
  { id: "terms", title: "Terms of service", short: "Terms" },
  { id: "privacy", title: "Privacy policy", short: "Privacy" },
  { id: "dpa", title: "Data processing agreement", short: "Data processing" },
  { id: "subprocessors", title: "Sub-processors", short: "Sub-processors" },
];
export const isLegalDoc = (v: unknown): v is LegalDocId => LEGAL_DOCS.some((d) => d.id === v);

export const TRIAL_CHOICES = [
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
  { value: "other", label: "Other number of days" },
  { value: "none", label: "No free trial" },
] as const;
export const NOTICE_CHOICES = [
  { value: "month_end", label: "End of current billing month" },
  { value: "30_days", label: "30 days" },
  { value: "year_end", label: "End of current billing year" },
] as const;
export type TrialChoice = (typeof TRIAL_CHOICES)[number]["value"];
export type NoticeChoice = (typeof NOTICE_CHOICES)[number]["value"];

export interface SubProcessor { name: string; purpose: string; location: string }
export interface LegalDetails {
  companyName: string;
  country: string;          // country of registration (its name)
  address: string;          // registered address
  registrationNumber: string;
  taxNumber: string;        // VAT / tax number
  supportEmail: string;
  privacyEmail: string;
  trial: TrialChoice | "";
  trialDays: number | null; // only with trial "other": 1–365
  notice: NoticeChoice | "";
  subprocessors: SubProcessor[];
}

export const DEFAULT_SUPPORT_EMAIL = "support@falconplm.com";
export const DEFAULT_PRIVACY_EMAIL = "privacy@falconplm.com";
/** How long a closed organisation stays read-only before it can be deleted permanently. */
export const CLOSE_READ_ONLY_DAYS = 30;
/** How long a "Download all our data" file can be downloaded. */
export const EXPORT_KEEP_DAYS = 7;
/** Who is named when the company name is missing. */
export const FALLBACK_NAME = "the operator of Falcon PLM";

/** Field labels, in form order (also the order of the "Legal details incomplete" list). */
export const LEGAL_FIELDS: Array<{ key: keyof LegalDetails; label: string; required: boolean }> = [
  { key: "companyName", label: "Legal company name", required: true },
  { key: "country", label: "Country of registration", required: true },
  { key: "address", label: "Registered address", required: true },
  { key: "registrationNumber", label: "Company registration number", required: false },
  { key: "taxNumber", label: "VAT/tax number", required: false },
  { key: "supportEmail", label: "Support email", required: true },
  { key: "privacyEmail", label: "Privacy contact email", required: true },
  { key: "trial", label: "Free trial", required: true },
  { key: "notice", label: "Cancellation notice", required: true },
];

const text = (v: unknown, max = 300) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : "");
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmail = (v: string) => EMAIL.test(v);

export function emptyLegalDetails(subprocessors: SubProcessor[] = []): LegalDetails {
  return {
    companyName: "", country: "", address: "", registrationNumber: "", taxNumber: "",
    supportEmail: DEFAULT_SUPPORT_EMAIL, privacyEmail: DEFAULT_PRIVACY_EMAIL,
    trial: "", trialDays: null, notice: "", subprocessors,
  };
}

/** Tidy whatever was stored or sent into a full set of details. */
export function normalizeLegalDetails(raw: any, fallbackSubprocessors: SubProcessor[] = []): LegalDetails {
  const r = raw && typeof raw === "object" ? raw : {};
  const trial = TRIAL_CHOICES.some((c) => c.value === r.trial) ? r.trial : "";
  const days = Number(r.trialDays);
  return {
    companyName: text(r.companyName, 200),
    country: text(r.country, 100),
    address: typeof r.address === "string" ? r.address.split(/\r?\n/).map((l: string) => text(l, 200)).filter(Boolean).join("\n").slice(0, 600) : "",
    registrationNumber: text(r.registrationNumber, 60),
    taxNumber: text(r.taxNumber, 60),
    supportEmail: text(r.supportEmail ?? DEFAULT_SUPPORT_EMAIL, 200).toLowerCase(),
    privacyEmail: text(r.privacyEmail ?? DEFAULT_PRIVACY_EMAIL, 200).toLowerCase(),
    trial,
    trialDays: trial === "other" && Number.isFinite(days) && r.trialDays !== "" && r.trialDays !== null ? Math.trunc(days) : null,
    notice: NOTICE_CHOICES.some((c) => c.value === r.notice) ? r.notice : "",
    subprocessors: Array.isArray(r.subprocessors)
      ? r.subprocessors.map((s: any) => ({ name: text(s?.name, 120), purpose: text(s?.purpose, 200), location: text(s?.location, 120) })).filter((s: SubProcessor) => s.name).slice(0, 50)
      : fallbackSubprocessors,
  };
}

/** The required details still missing (labels), for the red boxes and the Console banner. */
export function missingLegal(d: LegalDetails): string[] {
  const out: string[] = [];
  for (const f of LEGAL_FIELDS) {
    if (!f.required) continue;
    const v = d[f.key];
    if (f.key === "trial") {
      if (!d.trial || (d.trial === "other" && !validTrialDays(d.trialDays))) out.push(f.label);
    } else if (f.key === "supportEmail" || f.key === "privacyEmail") {
      if (!isEmail(String(v || ""))) out.push(f.label);
    } else if (!String(v || "").trim()) out.push(f.label);
  }
  return out;
}
export const validTrialDays = (n: number | null | undefined) => typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 365;

/** Problems that stop a save (wrong values, not missing ones): the form shows them on the box. */
export function legalProblems(d: LegalDetails): Array<{ field: string; message: string }> {
  const out: Array<{ field: string; message: string }> = [];
  if (d.supportEmail && !isEmail(d.supportEmail)) out.push({ field: "supportEmail", message: "Enter a valid support email address." });
  if (d.privacyEmail && !isEmail(d.privacyEmail)) out.push({ field: "privacyEmail", message: "Enter a valid privacy contact email address." });
  if (d.trial === "other" && !validTrialDays(d.trialDays)) out.push({ field: "trialDays", message: "Enter the trial length as a whole number of days from 1 to 365." });
  d.subprocessors.forEach((s, i) => {
    if (!s.purpose) out.push({ field: `subprocessors.${i}.purpose`, message: `Enter what ${s.name} does.` });
    if (!s.location) out.push({ field: `subprocessors.${i}.location`, message: `Enter where ${s.name} keeps the data.` });
  });
  return out;
}

/** The next version number: 1.0 first, then 1.1, 1.2 … 1.10 … */
export function nextLegalVersion(prev: string | null | undefined): string {
  const m = /^(\d+)\.(\d+)$/.exec(String(prev || ""));
  return m ? `${m[1]}.${Number(m[2]) + 1}` : "1.0";
}

/** The email sending service, named from the mail server address in the server's settings (SMTP_HOST). */
export function emailServiceFromHost(host: string): SubProcessor | null {
  const h = String(host || "").toLowerCase().trim();
  if (!h || h === "localhost" || h === "127.0.0.1") return null;
  const known: Array<[RegExp, string, string]> = [
    [/ionos|1and1|1und1/, "IONOS", "Europe"],
    [/sendgrid/, "SendGrid (Twilio)", "United States"],
    [/mailgun/, "Mailgun", "United States or Europe"],
    [/amazonaws|amazonses|\bses\b/, "Amazon Simple Email Service", "Region chosen in the Amazon account"],
    [/postmark/, "Postmark", "United States"],
    [/mailjet/, "Mailjet", "Europe"],
    [/brevo|sendinblue/, "Brevo", "Europe"],
    [/office365|outlook|microsoft/, "Microsoft 365", "Region of the Microsoft account"],
    [/gmail|google/, "Google Workspace", "Region of the Google account"],
    [/zoho/, "Zoho Mail", "Region of the Zoho account"],
  ];
  for (const [re, name, location] of known) if (re.test(h)) return { name, purpose: "Sending email (invitations, password resets and notices)", location };
  return { name: h, purpose: "Sending email (invitations, password resets and notices)", location: "Where the email provider keeps its servers" };
}

/** The starting sub-processors: IONOS for hosting and backups, and the email service the server is set up with. */
export function defaultSubprocessors(smtpHost: string): SubProcessor[] {
  const list: SubProcessor[] = [{ name: "IONOS", purpose: "Hosting and backups", location: "Europe" }];
  const mail = emailServiceFromHost(smtpHost);
  if (mail) {
    if (mail.name === "IONOS") list[0] = { name: "IONOS", purpose: "Hosting, backups and sending email", location: "Europe" };
    else list.push(mail);
  }
  return list;
}

// ---- The documents ------------------------------------------------------------------------------------------

export interface LegalSection { heading: string; paras?: string[]; list?: string[]; after?: string[]; table?: { head: string[]; rows: string[][] } }
export interface LegalDocument { id: LegalDocId; title: string; intro?: string; sections: LegalSection[] }
/** Facts that come from the server, not the form. */
export interface LegalContext {
  /** Days the nightly backups are kept on the server (BACKUP_KEEP_DAYS). */
  backupDays: number;
  /** Days the off-site copy is kept (OFFSITE_BACKUP_KEEP_DAYS), when known. */
  offsiteDays: number | null;
  /** When the sub-processor list last changed (dd Month yyyy). */
  subprocessorsChanged: string;
}

/** "9 October 2026" */
export function legalDate(at: string | Date): string {
  const d = typeof at === "string" ? new Date(at) : at;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function who(d: LegalDetails) {
  const name = d.companyName || FALLBACK_NAME;
  const address = d.address.split("\n").map((l) => l.trim()).filter(Boolean).join(", ");
  let provider = `The service is provided by ${name}`;
  if (d.country) provider += `, a company registered in ${d.country}`;
  if (d.registrationNumber) provider += `${d.country ? " with" : ", with"} company registration number ${d.registrationNumber}`;
  if (address) provider += `, whose registered address is ${address}`;
  provider += ".";
  const tax = d.taxNumber ? `Our VAT/tax number is ${d.taxNumber}.` : "";
  return { name, provider, tax };
}

/** How long backups keep a copy, in one sentence (from the server's backup set-up). */
export function backupSentence(c: LegalContext): string {
  const local = `Nightly backups are kept on the server for ${plural(c.backupDays, "day")} and then deleted automatically`;
  return c.offsiteDays
    ? `${local}; the off-site copy (IONOS Cloud Backup) is kept for ${plural(c.offsiteDays, "day")} and then rolls over in the same way.`
    : `${local}; the off-site copy kept by our hosting provider's backup service (IONOS Cloud Backup) rolls over on that service's schedule.`;
}
/** The longest a deleted organisation can stay in a backup. */
function backupGone(c: LegalContext): string {
  return c.offsiteDays
    ? `Copies in backups are removed as the backups roll over, within ${plural(Math.max(c.backupDays, c.offsiteDays), "day")}.`
    : `Copies in the server's backups are removed as they roll over, within ${plural(c.backupDays, "day")}; the off-site copy rolls over on its own schedule.`;
}

function trialSentences(d: LegalDetails): string[] {
  if (d.trial === "none") return ["We do not offer a free trial. Fees apply from the start of the subscription."];
  const days = d.trial === "other" ? (validTrialDays(d.trialDays) ? d.trialDays! : 0) : Number(d.trial || 0);
  if (!days) return [];
  return [
    `A new organisation can use Falcon PLM free for ${plural(days, "day")}. No fees are charged during the free trial.`,
    "At the end of the trial the organisation can subscribe, or stop using the service; if it stops, it can download its data and close the organisation as described below.",
  ];
}
function noticeSentence(d: LegalDetails): string {
  if (d.notice === "month_end") return "You can cancel at any time. The cancellation takes effect at the end of the current billing month.";
  if (d.notice === "30_days") return "You can cancel at any time by giving 30 days' notice.";
  if (d.notice === "year_end") return "You can cancel at any time. The cancellation takes effect at the end of the current billing year.";
  return "You can cancel at any time by contacting us.";
}
function locations(d: LegalDetails): string {
  const seen: string[] = [];
  for (const s of d.subprocessors) if (s.location && !seen.includes(s.location)) seen.push(s.location);
  return seen.join("; ");
}
const contact = (email: string, fallback: string) => `Questions? Contact ${isEmail(email) ? email : fallback}.`;

export function legalDocuments(d: LegalDetails, c: LegalContext): Record<LegalDocId, LegalDocument> {
  const { name, provider, tax } = who(d);
  const support = contact(d.supportEmail, DEFAULT_SUPPORT_EMAIL);
  const privacy = contact(d.privacyEmail, DEFAULT_PRIVACY_EMAIL);
  const trial = trialSentences(d);
  const where = locations(d);

  const terms: LegalDocument = {
    id: "terms", title: "Terms of service",
    sections: [
      { heading: "About these terms", paras: [
        "These terms of service (\"terms\") cover the use of Falcon PLM, an online service for planning, developing, sourcing and tracking products (\"the service\").",
        [provider, tax].filter(Boolean).join(" "),
        `In these terms "we" and "us" mean ${name}; "you" means the organisation that uses the service, and the people it invites.`,
      ] },
      { heading: "The service", paras: [
        "Falcon PLM lets your organisation keep its products, purchase orders, time and action plan, samples, quality control, fabric, finance, claims and partner records in one place, and share chosen records with the suppliers and agents it invites.",
        "We improve the service over time. We will not remove a main feature you rely on without telling you in advance.",
      ] },
      { heading: "Accounts and seats", paras: [
        "Your organisation's main admin decides who can use Falcon and what each person can see. Each person needs their own account; accounts must not be shared.",
        "Your subscription includes a number of user seats, which can be changed by asking us.",
        "You are responsible for keeping passwords and two-factor devices safe and for what is done with your accounts. Tell us straight away if you think an account has been misused.",
      ] },
      { heading: "Your data", paras: [
        "Your organisation owns all the records and files it puts into Falcon (\"your data\").",
        "We use your data only to provide the service to you, as described in the [Privacy policy](/legal/privacy) and the [Data processing agreement](/legal/dpa). We do not sell your data and do not use it for advertising.",
      ] },
      { heading: "Acceptable use", paras: ["When using the service you must not:"], list: [
        "break the law or anyone else's rights;",
        "upload viruses or other harmful code;",
        "try to reach another organisation's data, or test our security without our written permission;",
        "overload or disrupt the service;",
        "resell the service without our agreement.",
      ] },
      ...(trial.length ? [{ heading: "Free trial", paras: trial }] : []),
      { heading: "Fees", paras: [
        `${trial.length && d.trial !== "none" ? "After the free trial, fees" : "Fees"} are charged per user seat per month at the price agreed with your organisation, plus any taxes that apply. Invoices are payable by the date shown on them.`,
        "We give at least 30 days' notice before changing prices; new prices apply from the next billing period after the notice.",
      ] },
      { heading: "Cancellation", paras: [
        noticeSentence(d),
        "Cancelling does not delete your data straight away: see \"Closing your organisation and your data\" below.",
      ] },
      { heading: "Suspension", paras: [
        "We may suspend access, after warning you where we reasonably can, if fees are unpaid well after their due date, if the service is being misused, or if we must do so to protect the service or to follow the law.",
        "Your data is kept while access is suspended, and access comes back once the problem is resolved.",
      ] },
      { heading: "Closing your organisation and your data", paras: [
        "Your main admin can download all your data at any time as a .zip file: your records as spreadsheet (CSV) files and every uploaded file and photo.",
        `When your organisation is closed, it becomes read-only for ${CLOSE_READ_ONLY_DAYS} days: everyone can still sign in, look at the data and download it, but nothing can be changed. After that we delete your organisation's records and files permanently.`,
        backupGone(c),
      ] },
      { heading: "Availability and backups", paras: [
        "We aim to keep the service available at all times, apart from planned maintenance, which we announce in the app where we can. The service is provided on a best-effort basis; we do not promise it will always be available or free of errors.",
        `We back up all data every night. ${backupSentence(c)} Backups are for recovering the service after a failure; they do not replace your own downloads.`,
      ] },
      { heading: "Confidentiality", paras: [
        "Each of us keeps the other's confidential information private, uses it only for the purposes of these terms and shares it only with people who need it and must also keep it private.",
        "This does not apply to information that is already public, or that must be disclosed by law.",
      ] },
      { heading: "Liability", paras: [
        "Nothing in these terms limits liability that cannot be limited by law, such as liability for death or personal injury caused by negligence, or for fraud.",
        "Apart from that, neither of us is liable for indirect losses or for loss of profit, business or goodwill, and our total liability in any 12 months is limited to the fees you paid for the service in that period.",
        "You are responsible for the content you put into the service and for the decisions you make using it.",
      ] },
      { heading: "Changes to these terms", paras: [
        "We may update these terms. Each update has a new version number and date, shown at the top. When a change matters, everyone is asked to agree again the next time they sign in. Earlier versions stay available on this page.",
      ] },
      ...(d.country ? [{ heading: "Governing law", paras: [
        `These terms are governed by the law of ${d.country}, and the courts of ${d.country} can decide any dispute about them. This does not take away any right your organisation has under the law of its own country to bring a claim there.`,
      ] }] : []),
      { heading: "Contact", paras: [support] },
    ],
  };

  const privacyDoc: LegalDocument = {
    id: "privacy", title: "Privacy policy",
    sections: [
      { heading: "Who we are", paras: [
        `This privacy policy explains how ${name} ("we") handles personal information in Falcon PLM.`,
        [provider, tax].filter(Boolean).join(" "),
        "For the records your organisation keeps in Falcon, we act on your organisation's behalf, as its processor (see the [Data processing agreement](/legal/dpa)). For account, security and billing information, we decide how it is used, as the controller.",
      ] },
      { heading: "What we hold", list: [
        "names, work email addresses, roles and the organisation each person belongs to;",
        "sign-in records and the Activity Log (what was changed, by whom and when);",
        "the internet (IP) addresses used to sign in and to agree to these documents;",
        "photos and files that people upload, such as product images, quality-control photos, certificates and documents;",
        "the business records your organisation keeps in Falcon, which can include the names and contact details of suppliers, agents and other partners.",
      ], after: ["Falcon does not need sensitive personal information (such as health or religious beliefs); please do not upload it."] },
      { heading: "Why we use it", list: [
        "to provide the service your organisation has asked for;",
        "to keep accounts secure (sign-in, two-factor login and spotting misuse);",
        "to send service emails: invitations, password resets and the notices people choose;",
        "to answer support requests and to bill for the service;",
        "to meet our legal obligations.",
      ], after: ["We do not sell personal information, use it for advertising or build marketing profiles."] },
      { heading: "Lawful basis", paras: [
        "We use personal information because it is needed to provide the service (contract), for our legitimate interest in keeping the service secure and working well, and where the law requires it (legal obligation).",
        "Where a law, such as the Saudi Personal Data Protection Law or China's Personal Information Protection Law, needs consent or another specific basis for information your organisation puts into Falcon, your organisation (as the controller) is responsible for having it, and we help where we can.",
      ] },
      { heading: "Where it is stored", paras: [
        where
          ? `Data is stored and backed up with the providers listed on the [Sub-processors](/legal/subprocessors) page, in these locations: ${where}.`
          : "Data is stored and backed up with the providers listed on the [Sub-processors](/legal/subprocessors) page.",
        "If information is moved out of the country where it was collected, we use the safeguards the law requires, such as standard contractual clauses.",
      ] },
      { heading: "How long we keep it", paras: [
        "We keep your organisation's data while it uses Falcon, including its Activity Log and sign-in records.",
        `When an organisation is closed, its data stays available read-only for ${CLOSE_READ_ONLY_DAYS} days and is then deleted permanently. ${backupSentence(c)}`,
        "After a permanent delete we keep only a minimal record: the organisation's name, the dates it was closed and deleted, and who did it.",
      ] },
      { heading: "Security", paras: [
        "All connections to Falcon are encrypted (HTTPS). Passwords are stored only in a scrambled form that cannot be read back, two-factor login is available to everyone and required for admins, bank details are stored encrypted, and the database itself keeps each organisation's data separate. Data is backed up every night, and only a small number of authorised people can reach the servers.",
      ] },
      { heading: "Your rights", paras: [
        "Depending on where you live, you can ask to see the personal information we hold about you, have it corrected or deleted, object to or limit how it is used, and receive a copy in a portable form.",
        "If your information is part of your organisation's records, please ask your organisation first; we will help it respond.",
        "You can also complain to the data protection authority where you live, for example the Information Commissioner's Office in the United Kingdom, your national authority in the European Union, the Saudi Data and AI Authority (SDAIA) in Saudi Arabia, or the Cyberspace Administration of China.",
      ] },
      { heading: "Cookies", paras: [
        "Falcon uses only essential cookies: one cookie keeps you signed in, and it is removed when you sign out or it expires. Your browser also remembers a few display choices on your own device, such as light or dark theme and screen language.",
        "We do not use advertising, analytics or tracking cookies, so there is no cookie banner.",
      ] },
      { heading: "Changes to this policy", paras: ["If we change this policy, it gets a new version number and date at the top, and earlier versions stay available."] },
      { heading: "Contact", paras: [privacy] },
    ],
  };

  const dpa: LegalDocument = {
    id: "dpa", title: "Data processing agreement",
    sections: [
      { heading: "About this agreement", paras: [
        `This data processing agreement is part of the [Terms of service](/legal/terms) between ${name} ("we", the processor) and the organisation that uses Falcon PLM ("you", the controller). It covers personal information in the records your organisation puts into Falcon.`,
        "It is written to meet the requirements of UK and EU data protection law (GDPR Article 28), the Saudi Personal Data Protection Law and China's Personal Information Protection Law.",
      ] },
      { heading: "Who does what", paras: [
        "You decide which personal information goes into Falcon and why: you are the controller. We handle it only to provide the service: we are the processor.",
      ] },
      { heading: "Your instructions", paras: [
        "We handle personal information only on your documented instructions: these terms, how you use the service, and any other written instructions from your main admin.",
        "If we think an instruction breaks the law, we will tell you. We will handle the information in any other way only if the law requires it, and we will tell you first unless the law forbids that.",
      ] },
      { heading: "Confidentiality", paras: ["Everyone working for us who can reach personal information is bound to keep it confidential."] },
      { heading: "Security", paras: [
        "We keep suitable technical and organisational security measures, including encrypted connections, scrambled passwords, two-factor login, encrypted bank details, separation of each organisation's data in the database, access limited to authorised people, and nightly backups.",
      ] },
      { heading: "Sub-processors", paras: [
        "You agree that we may use the sub-processors listed on the [Sub-processors](/legal/subprocessors) page.",
        "We will give notice before we add or replace one, by updating that page and asking users to agree to the updated documents, so that you can object. Each sub-processor is bound by data protection terms at least as protective as these, and we remain responsible for its work.",
      ] },
      { heading: "Helping with people's rights", paras: [
        "We will help you answer requests from people using their rights (to see, correct, delete or move their information, or to object). Most can be done directly in Falcon by your admins, for example by editing or removing records, or downloading your data.",
      ] },
      { heading: "Personal data breaches", paras: [
        "If we become aware of a breach affecting your personal information, we will tell you without undue delay, with what we know about what happened, the information affected and what we are doing about it, so that you can meet your own duty to report it.",
      ] },
      { heading: "Deleting or returning data", paras: [
        "Your main admin can download all your data at any time.",
        `When your organisation is closed, the data stays available read-only for ${CLOSE_READ_ONLY_DAYS} days and is then deleted permanently. ${backupGone(c)}`,
        "We delete or return personal information at the end of the service, unless the law requires us to keep it.",
      ] },
      { heading: "Audits", paras: [
        "We will give you the information needed to show that we meet this agreement, and allow audits by you or an auditor you appoint, with reasonable notice, at reasonable times and subject to confidentiality. We may first answer written questions and provide documents, to keep disruption low.",
      ] },
      { heading: "Transfers between countries", paras: [
        "Where personal information is moved to another country, we make sure the law allows it, for example by using standard contractual clauses or the transfer rules of Saudi or Chinese law.",
      ] },
      { heading: "Contact", paras: [privacy] },
    ],
  };

  const subs: LegalDocument = {
    id: "subprocessors", title: "Sub-processors",
    intro: `These are the companies that help us provide Falcon PLM and may handle your organisation's data. We give notice before we add or replace one. List last changed: ${c.subprocessorsChanged}.`,
    sections: [
      { heading: "Current sub-processors", ...(d.subprocessors.length
        ? { table: { head: ["Name", "Purpose", "Location"], rows: d.subprocessors.map((s) => [s.name, s.purpose, s.location]) } }
        : { paras: ["We do not use any sub-processors at the moment."] }) },
      { heading: "Contact", paras: [support] },
    ],
  };

  return { terms, privacy: privacyDoc, dpa, subprocessors: subs };
}

// ---- HTML (the public pages and the Console preview) ---------------------------------------------------------

const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
/** Escaped text with [label](/legal/x) links and email addresses made clickable. */
export function legalInline(s: string): string {
  return escHtml(s)
    .replace(/\[([^\]]+)\]\((\/legal\/[a-z]+)\)/g, (_m, label, href) => `<a href="${href}">${label}</a>`)
    .replace(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, (m) => `<a href="mailto:${m}">${m}</a>`);
}
/** Plain text of a paragraph (links reduced to their words), for checks. */
export const legalPlain = (s: string) => s.replace(/\[([^\]]+)\]\((\/legal\/[a-z]+)\)/g, "$1");

/** The body of one document (title, version line, sections). */
export function legalDocHtml(doc: LegalDocument, version: string, updated: string): string {
  const ps = (list?: string[]) => (list || []).map((p) => `<p>${legalInline(p)}</p>`).join("");
  const sec = doc.sections.map((s) => `<section><h2>${escHtml(s.heading)}</h2>${ps(s.paras)}${s.list ? `<ul>${s.list.map((li) => `<li>${legalInline(li)}</li>`).join("")}</ul>` : ""}${ps(s.after)}${
    s.table ? `<div class="legal-table"><table><thead><tr>${s.table.head.map((h) => `<th>${escHtml(h)}</th>`).join("")}</tr></thead><tbody>${s.table.rows.map((r) => `<tr>${r.map((c) => `<td>${escHtml(c)}</td>`).join("")}</tr>`).join("")}</tbody></table></div>` : ""}</section>`).join("");
  return `<article class="legal-doc" data-doc="${doc.id}"><h1>${escHtml(doc.title)}</h1><p class="legal-meta">Version ${escHtml(version)} · Last updated ${escHtml(updated)}</p>${doc.intro ? `<p>${legalInline(doc.intro)}</p>` : ""}${sec}</article>`;
}

/** Every piece of text in a document, for checks (no blanks, brackets or placeholders). */
export function legalDocText(doc: LegalDocument): string {
  return [doc.title, doc.intro || "", ...doc.sections.flatMap((s) => [s.heading, ...(s.paras || []), ...(s.list || []), ...(s.after || []), ...(s.table ? [...s.table.head, ...s.table.rows.flat()] : [])])].filter(Boolean).map(legalPlain).join("\n");
}

